import React, { useEffect, useMemo, useRef, useState } from 'react'
import { isMobile } from 'react-device-detect'

import { useViewStore } from '../Contexts/ViewContext'
import { useDirectlineStore } from '../Contexts/DirectlineContext'
import { useRetorikStore } from '../Contexts/RetorikContext'
import { useUtilsStore } from '../Contexts/UtilsContext'

import useRefDimensions from '../../hooks/useRefDimensions'
import useCurrentPagination from '../../hooks/useCurrentPagination'

import type { WithChildren } from '../../models/utils'
import { ContainerParent, DeviceType, UIPart } from '../../models/enums'
import { mediumBreakpoint, mobileBreakpoint, widgetDisplays } from '../../models/constants'
import preventEvents from '../../utils/preventEvents'

import ModalFullscreen from '../Utils/ModalFullscreen'

type ContainerProps = WithChildren<{
  fullSize: boolean
  width?: number | string
  height?: number | string
  parent: number
}>

const Container = ({ fullSize, width, height, children, parent }: ContainerProps): JSX.Element => {
  const themeColors = useViewStore((state) => state.themeColors)
  const currentDeviceType = useViewStore((state) => state.currentDeviceType)
  const isTactile = useViewStore((state) => state.isTactile)
  const { setCurrentDeviceType, setCurrentHeight, setCurrentWidth } = useViewStore((state) => state.actions)
  const doNotDetectDeviceFromUserAgent = useRetorikStore((state) => state.configuration.doNotDetectDeviceFromUserAgent)
  const forceMobileView = useRetorikStore((state) => state.configuration.forceMobileView)
  const setClickedOnUI = useUtilsStore((state) => state.actions.setClickedOnUI)
  const setPaginationToSend = useDirectlineStore((state) => state.actions.setPaginationToSend)

  const container = useRef<HTMLDivElement>(null)
  const dimensions = useRefDimensions(container)
  const [portrait, setPortrait] = useState<string>('')
  const [large, setLarge] = useState<string>('')
  const [mobile, setMobile] = useState<string>('')
  const [tablet, setTablet] = useState<string>('')
  // Call useCurrentPagination to update data sent to store. Do not remove
  const pagination = useCurrentPagination()

  const widthStyle = useMemo<{ width?: string }>(() => {
    if (typeof width === 'number') {
      return { width: `${width}px` }
    } else if (typeof width === 'string') {
      return { width: width === 'full' ? '100%' : width }
    }

    return {}
  }, [width])

  const heightStyle = useMemo<{
    height?: string | number
    flexGrow?: number
    minHeight?: number
    maxHeight?: number
  }>(() => {
    if (currentDeviceType === DeviceType.mobile) {
      return {
        height: window.innerHeight,
        minHeight: window.innerHeight,
        maxHeight: window.innerHeight
      }
    } else if (typeof height === 'number') {
      return { height: `${height}px` }
    } else if (typeof height === 'string') {
      return height === 'full' ? { height: '100%', flexGrow: 1 } : { height: height }
    }

    return {}
  }, [height, currentDeviceType])

  useEffect(() => {
    setPaginationToSend(pagination)
  }, [pagination])

  const checkPortrait = (width: number, height: number): boolean => {
    return !!(width && height && width < height)
  }

  /**
   * On dimensions change :
   *  - check and set the classes in the main div that will allow us to use tailwind variants
   *  - set the main div dimensions in context
   */
  useEffect(() => {
    if (forceMobileView) {
      setCurrentDeviceType(DeviceType.mobile)
    } else if (dimensions?.width && dimensions?.height) {
      // If we don't want to use the userAgent to get the device type
      if (doNotDetectDeviceFromUserAgent) {
        const isPortrait = checkPortrait(dimensions.width, dimensions.height)

        if (isPortrait) {
          if (dimensions.width > mobileBreakpoint) {
            setCurrentDeviceType(parent === ContainerParent.widget ? DeviceType.widgetBorne : DeviceType.borne)
          } else {
            setCurrentDeviceType(parent === ContainerParent.widget ? DeviceType.widget : DeviceType.mobile)
          }
        } else {
          if (dimensions.height > mobileBreakpoint) {
            setCurrentDeviceType(parent === ContainerParent.widget ? DeviceType.widgetLandscape : DeviceType.landscape)
          } else {
            setCurrentDeviceType(parent === ContainerParent.widget ? DeviceType.widget : DeviceType.mobile)
          }
        }
      }
      // If we use the userAgent to get the device type
      else {
        if (isMobile) {
          setCurrentDeviceType(parent === ContainerParent.widget ? DeviceType.widget : DeviceType.mobile)
        } else {
          const isPortrait = checkPortrait(dimensions.width, dimensions.height)

          if (isPortrait) {
            setCurrentDeviceType(parent === ContainerParent.widget ? (dimensions.width > mobileBreakpoint ? DeviceType.widgetBorne : DeviceType.widget) : DeviceType.borne)
          } else {
            setCurrentDeviceType(parent === ContainerParent.widget ? DeviceType.widgetLandscape : DeviceType.landscape)
          }
        }
      }
    }

    dimensions?.height && setCurrentHeight(dimensions.height)
    dimensions?.width && setCurrentWidth(dimensions.width)
    setTablet(dimensions.height < mediumBreakpoint && dimensions.width < mediumBreakpoint ? 'rf-tablet' : '')
  }, [dimensions])

  useEffect(() => {
    switch (currentDeviceType) {
      case DeviceType.mobile:
      case DeviceType.widget:
        setPortrait('rf-portrait')
        setLarge('')
        setMobile('rf-mobile')
        setTablet('')
        break
      case DeviceType.landscape:
      case DeviceType.widgetLandscape:
        setPortrait('')
        setLarge('rf-large')
        setMobile('')
        break
      case DeviceType.borne:
      case DeviceType.widgetBorne:
        setPortrait('rf-portrait')
        setLarge('rf-large')
        setMobile('')
        break
    }
  }, [currentDeviceType])

  useEffect(() => {
    container?.current && widgetDisplays.includes(currentDeviceType) && preventEvents(container.current)
  }, [container.current])

  const customColors = useMemo<React.CSSProperties>(() => {
    return themeColors
      ? ({
          '--rf-color-primary': themeColors.primary,
          '--rf-color-secondary': themeColors.secondary,
          '--rf-color-black': themeColors.black,
          '--rf-color-whereToEatColor': themeColors.whereToEatColor,
          '--rf-color-whereToSleepColor': themeColors.whereToSleepColor,
          '--rf-color-tobeSeenColor': themeColors.tobeSeenColor,
          '--rf-color-tobeDoneColor': themeColors.tobeDoneColor,
          '--rf-color-localProductsColor': themeColors.localProductsColor,
          '--rf-color-servicesColor': themeColors.servicesColor,
          '--rf-color-cardFrameBackground': themeColors.card?.frame?.background,
          '--rf-color-cardFrameBorder': themeColors.card?.frame?.border,
          '--rf-color-cardFrameText': themeColors.card?.frame?.text,
          '--rf-color-cardButtonBackgroundDefault': themeColors.card?.button?.background?.default,
          '--rf-color-cardButtonBackgroundHover': themeColors.card?.button?.background?.hover,
          '--rf-color-cardButtonBorderDefault': themeColors.card?.button?.border?.default,
          '--rf-color-cardButtonBorderHover': themeColors.card?.button?.border?.hover,
          '--rf-color-cardButtonTextDefault': themeColors.card?.button?.text?.default,
          '--rf-color-cardButtonTextHover': themeColors.card?.button?.text?.hover,
          '--rf-color-cardButtonDiscoverBackgroundDefault': themeColors.card?.discoverButton?.background?.default,
          '--rf-color-cardButtonDiscoverBackgroundHover': themeColors.card?.discoverButton?.background?.hover,
          '--rf-color-cardButtonDiscoverBorderDefault': themeColors.card?.discoverButton?.border?.default,
          '--rf-color-cardButtonDiscoverBorderHover': themeColors.card?.discoverButton?.border?.hover,
          '--rf-color-cardButtonDiscoverTextDefault': themeColors.card?.discoverButton?.text?.default,
          '--rf-color-cardButtonDiscoverTextHover': themeColors.card?.discoverButton?.text?.hover,
          '--rf-color-textModePanelBackground': themeColors.textMode?.panel?.background,
          '--rf-color-textModePanelBorder': themeColors.textMode?.panel?.border,
          '--rf-color-textModePanelBackgroundUser': themeColors.textMode?.panel?.backgroundUser,
          '--rf-color-textModePanelBackgroundBot': themeColors.textMode?.panel?.backgroundBot,
          '--rf-color-textModePanelConversationUser': themeColors.textMode?.panel?.conversationUser,
          '--rf-color-textModePanelConversationBot': themeColors.textMode?.panel?.conversationBot,
          '--rf-color-vocalModeSubtitlesText': themeColors.vocalMode?.subtitles?.text,
          '--rf-color-vocalModeSubtitlesBackground': themeColors.vocalMode?.subtitles?.background,
          '--rf-color-formInputTextDefault': themeColors.formInput?.text?.default,
          '--rf-color-formInputTextHover': themeColors.formInput?.text?.hover,
          '--rf-color-formInputRadioCheckboxUncheckedBackground': themeColors.formInput?.inputRadioCheckbox?.unchecked?.background,
          '--rf-color-formInputRadioCheckboxUncheckedBorder': themeColors.formInput?.inputRadioCheckbox?.unchecked?.border,
          '--rf-color-formInputRadioCheckboxCheckedBackground': themeColors.formInput?.inputRadioCheckbox?.checked?.background,
          '--rf-color-formInputRadioCheckboxCheckedBorder': themeColors.formInput?.inputRadioCheckbox?.checked?.border,
          '--rf-color-formInputRadioCheckboxCheckedItem': themeColors.formInput?.inputRadioCheckbox?.checked?.item,
          '--rf-color-formInputButtonBackgroundDefault': themeColors.formInput?.inputButton?.background?.default,
          '--rf-color-formInputButtonBackgroundHover': themeColors.formInput?.inputButton?.background?.hover,
          '--rf-color-formInputButtonBorderDefault': themeColors.formInput?.inputButton?.border?.default,
          '--rf-color-formInputButtonBorderHover': themeColors.formInput?.inputButton?.border?.hover,
          '--rf-color-formInputButtonTextDefault': themeColors.formInput?.inputButton?.text?.default,
          '--rf-color-formInputButtonTextHover': themeColors.formInput?.inputButton?.text?.hover
        } as React.CSSProperties)
      : ({} as React.CSSProperties)
  }, [themeColors])

  return (
    <div
      id='retorik-container'
      ref={container}
      tabIndex={-1}
      className={`rf-relative ${
        fullSize ? 'rf-w-screen rf-h-screen' : 'rf-w-full rf-h-full'
      } rf-max-w-screen rf-max-h-screen rf-grid rf-grid-cols-8 rf-grid-rows-container rf-bg-transparent rf-text-trueblack rf-font-default ${portrait} ${large} ${mobile} ${tablet} ${
        isTactile && 'rf-tactile'
      } rf-overflow-hidden rf-animate-loaderFadeIn`}
      style={{
        ...widthStyle,
        ...heightStyle,
        ...customColors
      }}
      onClick={() => setClickedOnUI(UIPart.background)}
    >
      <ModalFullscreen />
      {children}
    </div>
  )
}

export default Container
